// 金额工具类
import { isNumber } from "./is"

// 数据缺省值
const DEFAULT_VALUE = "--";

// 格式化金额 后端数据转前端金额 字符串展示
export function formatMoney(
    transactionAmount: number | null | undefined,
    ratio: number = 1,
    digits: number = 2
): string {
    if (isNumber(transactionAmount)) {
        return Number(transactionAmount / ratio).toFixed(digits);
    }
    return DEFAULT_VALUE;
}

// 格式化金额 前端金额转后端数据 数字数据
export function formatAmount(transactionMoney: number | null, ratio: number = 1): number {
    if (isNumber(transactionMoney)) {
        return Number(transactionMoney * ratio);
    }
    return 0;
}

// 格式化金额 带千分位符
export function formatMoneyDigitGrouping(transactionAmount: number | null): string {
    if (transactionAmount && isNumber(transactionAmount)) {
        return transactionAmount
            .toLocaleString("zh-CN", {
                style: "currency",
                currency: "CNY"
            })
            .replace("¥", "");
    }
    return DEFAULT_VALUE;
}

export function digitUppercase(n: number): string {
    const fraction: string[] = ["角", "分"];
    const digit: string[] = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
    const unit: string[][] = [
        ["元", "万", "亿"],
        ["", "拾", "佰", "仟"]
    ];
    const head: string = n < 0 ? "负" : "";
    n = Math.abs(n);
    let s: string = "";
    for (let i = 0; i < fraction.length; i++) {
        s += (digit[Math.floor(n * 10 * Math.pow(10, i)) % 10] + fraction[i]).replace(/零./, "");
    }
    s = s || "整";
    n = Math.floor(n);
    for (let i = 0; i < unit[0].length && n > 0; i++) {
        let p: string = "";
        for (let j = 0; j < unit[1].length && n > 0; j++) {
            p = digit[n % 10] + unit[1][j] + p;
            n = Math.floor(n / 10);
        }
        s = p.replace(/(零.)*零$/, "").replace(/^$/, "零") + unit[0][i] + s;
    }
    return (
        head +
        s
            .replace(/(零.)*零元/, "元")
            .replace(/(零.)+/g, "零")
            .replace(/^整$/, "零元整")
    );
}

/**
 * 金额格式化工具函数
 * 规则：
 * - 整数 → 不带小数位（如 100 → "100"）
 * - 1位小数 → 补为2位（如 1.2 → "1.20"）
 * - 2位小数 → 正常显示（如 1.33 → "1.33"）
 * - 超过2位小数 → 四舍五入保留2位（如 1.456 → "1.46"）
 * - 25.00 → "25" 不保留小数点的0
 * - 3.50 → "3.5" 不保留小数点的0
 * @param amount 待格式化的金额（支持 number/string/BigDecimal 字符串）
 * @param defaultVal 金额无效时的默认返回值（默认：""）
 * @returns 格式化后的金额字符串
 */
export function formatCurrency(
  amount: number | string | undefined | null,
  defaultVal = ""
): string {
  // 1. 处理空值/无效值（undefined/null/空字符串）
  if (amount === undefined || amount === null || amount === "") {
    return defaultVal;
  }

  // 2. 统一转为数字（兼容字符串类型金额，如 "100"、"1.2"）
  const num = Number(amount);
  // 非数字值（如 "abc"、"12a"）直接返回默认值
  if (isNaN(num)) {
    return defaultVal;
  }

  // 3. 保留2位小数（自动四舍五入），先转为字符串
  const fixed2Str = num.toFixed(2);

  // 增加千分位
  const intPart = fixed2Str.split(".")[0];
  const floatPart = fixed2Str.split(".")[1];
  const intPartWithComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // 4. 处理小数部分：去掉末尾的零
  let formattedFloatPart = floatPart;
  // 如果小数部分以0结尾，去掉末尾的零
  if (floatPart.endsWith("00")) {
    return intPartWithComma;
  }

  // 5. 如果没有小数部分，只返回整数部分
  if (formattedFloatPart === "") {
    return intPartWithComma;
  }

  // 6. 返回带小数部分的格式化金额
  return intPartWithComma + "." + formattedFloatPart;
}